import Box from '@mui/material/Box'
import LinearProgress from '@mui/material/LinearProgress'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import { gridRowCountSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro'
import React from 'react'

import {
  CARD_BORDER_RADIUS,
  CARD_GAP,
  CARD_PADDING_X,
  CARD_PADDING_Y,
  CARD_TEXT_SPACING,
  SEVERITY_BADGE_SIZE,
  UNREAD_RAIL_WIDTH,
} from './notificationCenterConstants'

const PLACEHOLDER_COUNT = 6

/** Staggered so the column doesn't read as a block of identical bars. */
const TITLE_WIDTHS = ['58%', '44%', '66%', '38%', '52%', '48%']

const NotificationCardSkeleton = ({ titleWidth }: { titleWidth: string }) => (
  <Box
    sx={{
      width: '100%',
      mt: 0.75,
      mb: 0.75,
      border: 1,
      borderColor: 'divider',
      borderLeftWidth: UNREAD_RAIL_WIDTH,
      borderRadius: CARD_BORDER_RADIUS,
    }}
  >
    <Stack direction="row" spacing={CARD_GAP} sx={{ alignItems: 'flex-start', px: CARD_PADDING_X, py: CARD_PADDING_Y }}>
      <Skeleton height={24} variant="circular" width={24} />
      <Skeleton height={SEVERITY_BADGE_SIZE} variant="circular" width={SEVERITY_BADGE_SIZE} />

      <Stack spacing={CARD_TEXT_SPACING} sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton height={20} variant="rounded" width={72} />
        <Skeleton height={20} variant="text" width={titleWidth} />
        <Skeleton height={16} variant="text" width="82%" />
      </Stack>

      <Stack alignItems="flex-end" spacing={0.75} sx={{ flexShrink: 0 }}>
        <Skeleton height={14} variant="text" width={132} />
        <Skeleton height={22} variant="rounded" width={76} />
      </Stack>
    </Stack>
  </Box>
)

/**
 * Grid loading overlay. With rows already on screen a refetch only needs the bar —
 * replacing a populated feed with placeholders reads as the list being wiped.
 */
const NotificationFeedSkeleton = () => {
  const apiRef = useGridApiContext()
  const rowCount = useGridSelector(apiRef, gridRowCountSelector)

  if (rowCount > 0) return <LinearProgress />

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'hidden', bgcolor: 'background.paper' }}>
      {TITLE_WIDTHS.slice(0, PLACEHOLDER_COUNT).map(width => (
        <NotificationCardSkeleton key={width} titleWidth={width} />
      ))}
    </Box>
  )
}

export default NotificationFeedSkeleton
